// web/src/state/OfflineQueue.ts
import { openDb, reqAsPromise, txDone } from "./idb";
import type { Mutation } from "./AnnotationStore";
import type { PanoView } from "../api/types";

const DB_NAME = "vmc-offline";
const DB_VERSION = 1;

export interface PersistedMutation {
  id: number;
  flightId: string;
  reviewerId: string;
  team: string;
  mutation: Mutation;
  clientTs: string;
  attempts: number;
  lastError?: string;
}

export interface PersistedFrameBlob {
  key: string;
  flightId: string;
  frameId: string;
  variant: "full" | "thumb";
  team: string;
  reviewerId: string;
  contentType: "image/jpeg";
  blob: Blob;
  clientTs: string;
  attempts: number;
  lastError?: string;
}

export interface OfflineQueueOpts {
  flightId: string;
  reviewerId: string;
  team: string;
  /** Drain annotation mutations. Returns the new ETag on 200, "conflict" on 412, throws on error. */
  flushAnnotations: (mutations: PersistedMutation[]) => Promise<{ kind: "ok"; etag: string } | { kind: "conflict" }>;
  /** Drain a single frame: presign + PUT both variants. */
  flushFrame: (rows: PersistedFrameBlob[]) => Promise<void>;
  /** Called once when a sub-request returns 401. */
  onAuthExpired: () => void;
  /** Fired whenever the queue's contents may have changed (enqueue or drain),
   *  so observers can re-read listMutations() for an accurate pending count. */
  onQueueChanged?: () => void;
  /** Override retry backoff steps in ms. Defaults to production schedule. Tests use small values. */
  backoffMs?: readonly number[];
}

export interface DrainResult {
  attempted: number;
  flushed: number;
}

export class AuthExpiredError extends Error {
  authExpired = true as const;
  constructor() { super("auth expired"); }
}

export class OfflineQueue {
  private constructor(private readonly db: IDBDatabase, private readonly opts: OfflineQueueOpts) {}

  private draining = false;

  private static readonly DEFAULT_BACKOFF_MS = [1000, 2000, 5000, 15000, 60000] as const;
  private failureCount = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private onlineHandler: (() => void) | null = null;
  private visibilityHandler: (() => void) | null = null;

  /** Run a drain nobody awaits.
   *
   *  These are triggered by browser events and retry timers, so a rejection
   *  would escape as an unhandled rejection. The database can legitimately
   *  disappear mid-drain — tab eviction, the user clearing storage, or a test
   *  tearing the connection down between cases — and the queue retries on the
   *  next trigger, so absorb the failure rather than leaving it dangling.
   */
  private drainInBackground(): void {
    void this.drain().catch(() => undefined);
  }

  start(): void {
    if (this.onlineHandler !== null) return;
    this.onlineHandler = () => { this.drainInBackground(); };
    this.visibilityHandler = () => {
      if (document.visibilityState === "visible") this.drainInBackground();
    };
    window.addEventListener("online", this.onlineHandler);
    document.addEventListener("visibilitychange", this.visibilityHandler);
  }

  stop(): void {
    if (this.onlineHandler !== null) {
      window.removeEventListener("online", this.onlineHandler);
      this.onlineHandler = null;
    }
    if (this.visibilityHandler !== null) {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
      this.visibilityHandler = null;
    }
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  private scheduleDrain(delayMs: number): Promise<void> {
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    return new Promise((resolve) => {
      this.retryTimer = setTimeout(() => {
        this.retryTimer = null;
        void this.drain().then(
          () => resolve(),
          () => resolve(),
        );
      }, delayMs);
    });
  }

  private scheduleNextAttempt(success: boolean): void {
    if (success) {
      this.failureCount = 0;
      return;
    }
    const schedule = this.opts.backoffMs ?? OfflineQueue.DEFAULT_BACKOFF_MS;
    const idx = Math.min(this.failureCount, schedule.length - 1);
    const delay = schedule[idx]!;
    this.failureCount += 1;
    void this.scheduleDrain(delay);
  }

  static async open(opts: OfflineQueueOpts): Promise<OfflineQueue> {
    const db = await openDb(DB_NAME, DB_VERSION, (db) => {
      const m = db.createObjectStore("mutations", { keyPath: "id", autoIncrement: true });
      m.createIndex("by_flight_reviewer", ["flightId", "reviewerId"], { unique: false });
      const f = db.createObjectStore("frameBlobs", { keyPath: "key" });
      f.createIndex("by_flight", "flightId", { unique: false });
      f.createIndex("by_frame", ["flightId", "frameId"], { unique: false });
      db.createObjectStore("meta", { keyPath: "key" });
    });
    return new OfflineQueue(db, opts);
  }

  async close(): Promise<void> {
    this.db.close();
  }

  async enqueueMutation(mutation: Mutation, clientTs: string): Promise<void> {
    const tx = this.db.transaction("mutations", "readwrite");
    const row: Omit<PersistedMutation, "id"> = {
      flightId: this.opts.flightId,
      reviewerId: this.opts.reviewerId,
      team: this.opts.team,
      mutation,
      clientTs,
      attempts: 0,
    };
    tx.objectStore("mutations").add(row as PersistedMutation);
    await txDone(tx);
    this.opts.onQueueChanged?.();
  }

  async listMutations(): Promise<PersistedMutation[]> {
    const tx = this.db.transaction("mutations", "readonly");
    const idx = tx.objectStore("mutations").index("by_flight_reviewer");
    const rows = await reqAsPromise(idx.getAll(IDBKeyRange.only([this.opts.flightId, this.opts.reviewerId])));
    return rows as PersistedMutation[];
  }

  async enqueueFrame(args: {
    frameId: string;
    fullBlob: Blob;
    thumbBlob: Blob;
    clientTs: string;
    meta: {
      videoTimeS: number;
      utc: number;
      lat: number;
      lon: number;
      poiId?: string;
      source?: "video" | "panorama";
      panoramaId?: string;
      panoView?: PanoView;
    };
    capturedBy: { reviewerId: string; displayName: string };
  }): Promise<void> {
    const { flightId, reviewerId, team } = this.opts;
    const { frameId, fullBlob, thumbBlob, clientTs, meta, capturedBy } = args;
    const tx = this.db.transaction(["frameBlobs", "mutations"], "readwrite");
    const blobs = tx.objectStore("frameBlobs");
    const mutations = tx.objectStore("mutations");

    const baseBlob = { flightId, frameId, team, reviewerId, clientTs, attempts: 0, contentType: "image/jpeg" as const };
    blobs.put({ ...baseBlob, key: `${flightId}:${frameId}:full`, variant: "full", blob: fullBlob });
    blobs.put({ ...baseBlob, key: `${flightId}:${frameId}:thumb`, variant: "thumb", blob: thumbBlob });

    mutations.add({
      flightId, reviewerId, team, clientTs, attempts: 0,
      mutation: {
        kind: "upsertFrame",
        frame: {
          id: frameId,
          schemaVersion: 1,
          videoTimeS: meta.videoTimeS,
          utc: meta.utc,
          lat: meta.lat,
          lon: meta.lon,
          poiId: meta.poiId,
          source: meta.source,
          panoramaId: meta.panoramaId,
          panoView: meta.panoView,
          capturedAt: clientTs,
          capturedBy,
          hasFull: false,
          hasThumb: false,
        },
      },
    } as PersistedMutation);
    await txDone(tx);
    this.opts.onQueueChanged?.();
  }

  async listFrameBlobs(frameId: string): Promise<PersistedFrameBlob[]> {
    const tx = this.db.transaction("frameBlobs", "readonly");
    const idx = tx.objectStore("frameBlobs").index("by_frame");
    const rows = await reqAsPromise(idx.getAll(IDBKeyRange.only([this.opts.flightId, frameId])));
    return rows as PersistedFrameBlob[];
  }

  async drain(): Promise<DrainResult> {
    if (this.draining) return { attempted: 0, flushed: 0 };
    this.draining = true;
    let success = false;
    let authExpiredHit = false;
    let result: DrainResult = { attempted: 0, flushed: 0 };
    try {
      await this.drainFrames();
      const mutations = await this.listMutations();
      if (mutations.length === 0) {
        success = true;
        return { attempted: 0, flushed: 0 };
      }
      try {
        const r = await this.opts.flushAnnotations(mutations);
        if (r.kind === "ok") {
          await this.deleteMutationsById(mutations.map((m) => m.id));
          success = true;
          result = { attempted: mutations.length, flushed: mutations.length };
        } else {
          result = { attempted: mutations.length, flushed: 0 };
        }
      } catch (err) {
        if ((err as { authExpired?: boolean }).authExpired === true) {
          this.opts.onAuthExpired();
          authExpiredHit = true;
          return { attempted: mutations.length, flushed: 0 };
        }
        // Fire-and-forget the attempts/lastError bookkeeping so retries
        // don't depend on this commit (e.g. under fake timers in tests).
        // It is best-effort by design, and the database can legitimately be
        // gone by the time it commits (tab eviction, cleared storage, or a
        // test tearing the connection down mid-drain), so swallow its
        // rejection rather than leaving an unhandled one behind.
        void this.recordMutationError(mutations, (err as Error).message ?? "unknown").catch(
          () => undefined,
        );
        result = { attempted: mutations.length, flushed: 0 };
      }
      return result;
    } finally {
      this.draining = false;
      if (this.onlineHandler !== null && !authExpiredHit) this.scheduleNextAttempt(success);
      this.opts.onQueueChanged?.();
    }
  }

  private async drainFrames(): Promise<void> {
    const tx = this.db.transaction("frameBlobs", "readonly");
    const all = (await reqAsPromise(
      tx.objectStore("frameBlobs").index("by_flight").getAll(IDBKeyRange.only(this.opts.flightId)),
    )) as PersistedFrameBlob[];
    const byFrame = new Map<string, PersistedFrameBlob[]>();
    for (const row of all) {
      const list = byFrame.get(row.frameId) ?? [];
      list.push(row);
      byFrame.set(row.frameId, list);
    }
    for (const [frameId, rows] of byFrame.entries()) {
      if (rows.length < 2) continue; // wait until both variants are queued
      try {
        await this.opts.flushFrame(rows);
        await this.deleteFrameBlobs(frameId);
        await this.markFrameMutationUploaded(frameId);
      } catch (err) {
        if ((err as { authExpired?: boolean }).authExpired === true) {
          this.opts.onAuthExpired();
          return;
        }
        await this.recordFrameError(rows, (err as Error).message ?? "unknown");
      }
    }
  }

  private async deleteMutationsById(ids: number[]): Promise<void> {
    const tx = this.db.transaction("mutations", "readwrite");
    for (const id of ids) tx.objectStore("mutations").delete(id);
    await txDone(tx);
  }

  private async recordMutationError(rows: PersistedMutation[], msg: string): Promise<void> {
    const tx = this.db.transaction("mutations", "readwrite");
    for (const r of rows) {
      tx.objectStore("mutations").put({ ...r, attempts: r.attempts + 1, lastError: msg });
    }
    await txDone(tx);
  }

  private async deleteFrameBlobs(frameId: string): Promise<void> {
    const tx = this.db.transaction("frameBlobs", "readwrite");
    tx.objectStore("frameBlobs").delete(`${this.opts.flightId}:${frameId}:full`);
    tx.objectStore("frameBlobs").delete(`${this.opts.flightId}:${frameId}:thumb`);
    await txDone(tx);
  }

  private async recordFrameError(rows: PersistedFrameBlob[], msg: string): Promise<void> {
    const tx = this.db.transaction("frameBlobs", "readwrite");
    for (const r of rows) {
      tx.objectStore("frameBlobs").put({ ...r, attempts: r.attempts + 1, lastError: msg });
    }
    await txDone(tx);
  }

  /**
   * After both blob variants for a frame have uploaded, patch the queued
   * upsertFrame mutation so hasFull/hasThumb flip true on the next anno flush.
   */
  private async markFrameMutationUploaded(frameId: string): Promise<void> {
    const tx = this.db.transaction("mutations", "readwrite");
    const idx = tx.objectStore("mutations").index("by_flight_reviewer");
    const rows = (await reqAsPromise(
      idx.getAll(IDBKeyRange.only([this.opts.flightId, this.opts.reviewerId])),
    )) as PersistedMutation[];
    for (const row of rows) {
      if (row.mutation.kind !== "upsertFrame") continue;
      if (row.mutation.frame.id !== frameId) continue;
      const updated: PersistedMutation = {
        ...row,
        mutation: {
          kind: "upsertFrame",
          frame: { ...row.mutation.frame, hasFull: true, hasThumb: true },
        },
      };
      tx.objectStore("mutations").put(updated);
    }
    await txDone(tx);
  }
}
